import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

import { NamedLink, PrimaryButton } from '../../components';

import { askAboutPiece } from './CollectionPage.duck';

import css from './CollectionInterestForm.module.css';

/**
 * Nextmine: "Jeg er interesseret" på ét samlingsstykke.
 *
 * Samme handling to steder - i samlingens detalje-visning og på stykkets egen
 * side - så den bor ét sted. Ellers ville de to knapper langsomt drive fra
 * hinanden i ordlyd og opførsel.
 *
 * Noten er valgfri med vilje: interessen er signalet, teksten er pynt. Siger
 * man ingenting, sender vi en pæn standardlinje, så ét klik er nok. Handlingen
 * åbner en inquiry-tråd i ejerens indbakke - ingen betaling, intet løfte.
 *
 * @param {Object} props
 * @param {Object} props.piece samlingsstykket
 * @param {string} props.collectorName ejerens fornavn (til teksterne)
 * @param {boolean} props.isAuthenticated
 */
const CollectionInterestForm = props => {
  const { className, rootClassName, piece, collectorName, isAuthenticated } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const [note, setNote] = useState('');

  const { askInProgress, askError, askSentForPieceId } = useSelector(state => state.CollectionPage);

  const pieceId = piece?.id?.uuid;
  // Kun "sendt" for netop dette stykke - ellers slæber kvitteringen med fra et
  // stykke man kiggede på tidligere i samme besøg.
  const hasSent = !!pieceId && askSentForPieceId === pieceId;

  const classes = classNames(rootClassName || css.root, className);

  const handleSubmit = e => {
    e.preventDefault();
    const trimmed = note.trim();
    const message = trimmed || intl.formatMessage({ id: 'CollectionInterest.defaultMessage' });
    // Fejlen lander i CollectionPage-state og vises herunder.
    dispatch(askAboutPiece(piece, message)).catch(() => {});
  };

  if (hasSent) {
    return (
      <div className={classNames(classes, css.sentNote)}>
        <FormattedMessage id="CollectionInterest.sent" values={{ name: collectorName }} />
        <NamedLink className={css.inboxLink} name="InboxPage" params={{ tab: 'orders' }}>
          <FormattedMessage id="CollectionInterest.inboxLink" />
        </NamedLink>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <NamedLink className={classNames(classes, css.signupLink)} name="SignupPage">
        <FormattedMessage id="CollectionInterest.signup" />
      </NamedLink>
    );
  }

  const inputId = `collection-interest-note-${pieceId || 'piece'}`;

  return (
    <form className={classes} onSubmit={handleSubmit}>
      <label className={css.noteLabel} htmlFor={inputId}>
        <FormattedMessage id="CollectionInterest.noteLabel" />
      </label>
      <textarea
        id={inputId}
        className={css.noteInput}
        rows={3}
        value={note}
        onChange={e => setNote(e.target.value)}
        placeholder={intl.formatMessage({ id: 'CollectionInterest.notePlaceholder' })}
      />
      {askError ? (
        <p className={css.error}>
          <FormattedMessage id="CollectionInterest.failed" />
        </p>
      ) : null}
      <PrimaryButton type="submit" inProgress={askInProgress}>
        <FormattedMessage id="CollectionInterest.submit" />
      </PrimaryButton>
    </form>
  );
};

export default CollectionInterestForm;
